import {
  Autocomplete,
  Chip,
  FormControl,
  FormHelperText,
  TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';
import { ResourceVisibility } from '../../../interfaces/ResourceAccess';
import { getUsers, RosterMember } from '../../../services/inviteService';
import StyledFormItem from '../../../styles/StyledFormItem';
import { TranslationKey } from '../../../types/i18n';
import Dropdown from '../DropDown/DropDown';
import Text from '../Text/Text';
import ToolTip from '../ToolTip/ToolTip';

interface ResourceAccessFieldsProps {
  ownerUserId?: string;
  resourceLabelKey?: TranslationKey;
}

const visibilityOptions: {
  label: TranslationKey;
  value: ResourceVisibility;
}[] = [
  { label: 'Private', value: 'private' },
  { label: 'Company', value: 'company' },
  { label: 'Shared', value: 'shared' },
];

const visibilityHelpText: Record<ResourceVisibility, TranslationKey> = {
  private: 'Only you can view and edit this {{resource}}.',
  company: 'All coworkers in your company can view this {{resource}}.',
  shared:
    'Choose coworkers in your company who can view and edit this {{resource}}.',
};

const ResourceAccessFields: React.FC<ResourceAccessFieldsProps> = ({
  ownerUserId,
  resourceLabelKey = 'resource',
}) => {
  const { t } = useTranslation();
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [companyUsers, setCompanyUsers] = useState<RosterMember[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const visibility = watch('access.visibility') as
    | ResourceVisibility
    | undefined;
  const resourceLabel = t(resourceLabelKey);
  const effectiveVisibility = visibility ?? 'private';

  useEffect(() => {
    let cancelled = false;
    setUsersLoading(true);
    getUsers()
      .then(users => {
        if (!cancelled) {
          setCompanyUsers(users);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCompanyUsers([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setUsersLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (visibility !== 'shared') {
      setValue('access.shared_with_user_ids', [], { shouldDirty: true });
    }
  }, [visibility, setValue]);

  const selectableUsers = useMemo(
    () => companyUsers.filter(user => user.id !== ownerUserId),
    [companyUsers, ownerUserId]
  );

  const accessErrors = errors.access as
    | {
        visibility?: { message?: string };
        shared_with_user_ids?: { message?: string };
      }
    | undefined;

  return (
    <>
      <StyledFormItem>
        <Text type="label">
          {t('Visibility')}
          <ToolTip
            title={t(
              'Control who in your company can view and use this {{resource}}.',
              { resource: resourceLabel }
            )}
          />
        </Text>
        <Dropdown
          name="access.visibility"
          required
          initialValue="private"
          options={visibilityOptions.map(option => ({
            label: t(option.label),
            value: option.value,
          }))}
          fullWidth
          withError={!!accessErrors?.visibility}
          helperText={
            accessErrors?.visibility?.message ??
            t(visibilityHelpText[effectiveVisibility], {
              resource: resourceLabel,
            })
          }
        />
      </StyledFormItem>

      {visibility === 'shared' && (
        <StyledFormItem>
          <Text type="label">
            {t('Share with coworkers in your company')}
            <ToolTip
              title={t(
                'Selected coworkers in your company can view and edit this {{resource}}.',
                { resource: resourceLabel }
              )}
            />
          </Text>
          <Controller
            name="access.shared_with_user_ids"
            control={control}
            rules={{
              validate: value =>
                visibility !== 'shared' ||
                (Array.isArray(value) && value.length > 0) ||
                t('Select at least one coworker in your company to share with'),
            }}
            render={({ field }) => (
              <FormControl
                fullWidth
                error={!!accessErrors?.shared_with_user_ids}
              >
                <Autocomplete
                  multiple
                  options={selectableUsers}
                  getOptionLabel={option => `${option.name} (${option.email})`}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectableUsers.filter(user =>
                    (field.value ?? []).includes(user.id)
                  )}
                  onChange={(_, selectedUsers) => {
                    field.onChange(selectedUsers.map(user => user.id));
                  }}
                  disabled={usersLoading}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                      />
                    ))
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder={
                        usersLoading
                          ? t('Loading coworkers...')
                          : t('Select coworkers in your company')
                      }
                    />
                  )}
                />
                <FormHelperText>
                  {accessErrors?.shared_with_user_ids?.message ??
                    t('Only coworkers in your company are listed.')}
                </FormHelperText>
              </FormControl>
            )}
          />
        </StyledFormItem>
      )}
    </>
  );
};

export default ResourceAccessFields;
